import { FC, useCallback, useEffect, useState } from 'react'
import { useRequest } from 'ahooks'

import { Spinner } from '@trustycars/genie-ui/components'
import { Icon } from '@trustycars/genie-ui/icons/Icon'

import { buildURL, toast, asyncSuccessResolver } from '$app/utilities'

import usePageClass from '$browser/usePageClass'
import useRouter from '$actions/useRouter'
import useSignal from '$actions/useSignal'

import queryParams from '$store/queryParams'
import { status } from '$store/status'

import api from '$model/api'
import { GetCodeRequest } from '$api/types'

import { ToastMessage } from '$app/types'

import { errorMessageResolver, successResolver } from '$services/api/common'

import { useMFABlock } from '$actions/useMFABlock'
import { use2FAHelper } from '$actions/use2FAHelper'
import useCurrentAuthRTC from '$actions/useCurrentAuthRTC'

import { MFABlock } from '$blocks/MFABlock'

import { content, container, manualRedirectLink } from './styles.css'

const pageName = 'App'

const getCodeReqToastMsg: ToastMessage = {
  loading: 'Getting code..',
  error: errorMessageResolver,
  success: 'Code successfully retrieved.'
}

const notRedirectMsg = 'Not redirecting? Please follow the button below.'

export const App: FC = () => {
  usePageClass({ name: pageName })

  const { redirect, redirectWithReturnTo } = useRouter()

  const {
    auth: { getCode },
    mfaAuth: apiInstance
  } = useSignal(api)
  const authRTC = useCurrentAuthRTC()

  const { hasApiActiveSource } = useSignal(status)
  const { params } = useSignal(queryParams)

  const [callbackURL, setCallbackURL] = useState<URL>()
  const [message, setMessage] = useState('Connecting your service...')

  const isStuck = callbackURL && message === notRedirectMsg

  const getCodeReq = useRequest(
    asyncSuccessResolver(getCode, successResolver),
    {
      manual: true,
      onError: err => {
        if (twoFAHelper.is2FAError(err)) {
          mfaBlock.modalControls.open()
        } else {
          const message = errorMessageResolver(
            err,
            'Failed to reach service. Please contact support.'
          )

          redirectWithReturnTo('authInvalidAccess', 'applications', {
            queryParams: { message },
            replace: true
          })
        }
      },
      onSuccess: async ({ data: { code } }, [{ redirect_uri, state }]) => {
        if (redirect_uri) {
          const URL = buildURL(redirect_uri, {
            queryParams: { code, state }
          })

          setMessage('Redirecting to service..')
          setCallbackURL(URL)

          window.location.replace(URL)

          setTimeout(() => {
            setMessage(notRedirectMsg)
          }, 3000)
        }
      }
    }
  )

  const twoFAHelper = use2FAHelper({ getMfaBlock: () => mfaBlock })
  const preferred_factor = twoFAHelper.getPreferredFactorByError(
    getCodeReq.error
  )
  const available_factors = twoFAHelper.getFactorsByError(getCodeReq.error)

  const mfaBlock = useMFABlock({
    apiInstance,
    authRTC,
    data: { available_factors, preferred_factor },
    onCancelled: () => redirect('applications'),
    onVerified: mfa_token => {
      mfaBlock.modalControls.close()

      const { params, runAsync } = getCodeReq

      // Make sure there was a previous call
      if (!params.length) return

      const [data] = params

      // Auto retry the request with token
      toast
        .promise(runAsync({ ...data, mfa_token }), getCodeReqToastMsg)
        .unwrap()
        .catch(twoFAHelper.reChallengeOrExitOnError)
    }
  })

  const connect = useCallback(
    async () => {
      const {
        client_id,
        code_challenge,
        code_challenge_method,
        redirect_uri,
        response_type,
        scope,
        state
      } = params as GetCodeRequest

      getCodeReq.runAsync({
        client_id,
        code_challenge,
        code_challenge_method,
        redirect_uri,
        response_type,
        scope,
        state
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params]
  )

  useEffect(() => {
    if (!hasApiActiveSource) return

    connect()
  }, [hasApiActiveSource, connect])

  return (
    <article className={container}>
      <Spinner />

      <main className={content}>
        <p>{message}</p>
      </main>

      {isStuck && (
        <footer>
          <a href={callbackURL.href} className={manualRedirectLink}>
            Continue to service <Icon namespace="Right" />
          </a>
        </footer>
      )}

      <MFABlock {...mfaBlock} />
    </article>
  )
}

export default App
